import { useEffect } from 'react';

/**
 * useEventSchemaMonitor
 * Monitors whether Event JSON-LD is properly injected into the DOM
 * Useful for debugging missing schema in production
 * 
 * ✅ Verifies script tag exists
 * ✅ Validates JSON structure
 * ✅ Reports errors in development
 */
export function useEventSchemaMonitor(eventId: string, enabled: boolean = true) {
  useEffect(() => {
    if (!enabled || process.env.NODE_ENV !== 'development') {
      return;
    }

    // Delay to allow Helmet to inject script
    const timeoutId = setTimeout(() => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      let foundEventSchema = false;

      scripts.forEach((script) => {
        try {
          const json = JSON.parse(script.textContent || '{}');
          if (json['@type'] === 'Event' && json.name) {
            foundEventSchema = true;
            console.log(`✓ [Event Schema] Found Event schema for event ${eventId}:`, json);
          }
        } catch (error) {
          console.error(`[Event Schema] Failed to parse JSON-LD script:`, error, script.textContent);
        }
      });

      if (!foundEventSchema) {
        console.warn(
          `⚠️ [Event Schema] No Event JSON-LD found in DOM for event ${eventId}. ` +
          `Total ld+json scripts: ${scripts.length}`
        );
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [eventId, enabled]);
}

/**
 * Validates Event JSON-LD in the DOM at runtime
 * Returns true if valid Event schema is present and complete
 */
export function validateEventSchemaInDOM(): boolean {
  const scripts = document.querySelectorAll('script[type="application/ld+json"]');

  for (const script of scripts) {
    try {
      const json = JSON.parse(script.textContent || '{}');

      // Check for Event type
      if (json['@type'] === 'Event') {
        // Validate required fields
        const requiredFields = ['name', 'description', 'startDate', '@context'];
        const hasAllRequired = requiredFields.every(field => json[field]);

        if (hasAllRequired) {
          return true;
        }
      }
    } catch {
      // Skip invalid JSON
    }
  }

  return false;
}
